import type { ForumBase } from '../types/forum';
import { calculateForumScore } from './forumScore';
import { getCategoryPeers, getForumRank } from './forumRank';

export interface CategoryBenchmark {
  peerCount: number;
  avgMembers: number;
  avgPosts: number;
  avgScore: number;
  memberPercentile: number;
  postsPercentile: number;
  scorePercentile: number;
  memberRank: number;
  postsRank: number;
  scoreRank: number;
  forumMembers: number;
  forumPosts: number;
  forumScore: number;
}

export type BenchmarkMetric = 'members' | 'posts' | 'score';

export interface MetricComparison {
  key: BenchmarkMetric;
  label: string;
  forumValue: number;
  avgValue: number;
  percentile: number;
  rank: number;
  total: number;
  /** Ortalamaya göre yüzde fark; ortalama 0 ise null */
  vsAvgPercent: number | null;
}

function percentile(value: number, values: number[]): number {
  if (values.length === 0) return 0;
  const below = values.filter((v) => v <= value).length;
  return Math.round((below / values.length) * 100);
}

export function computeCategoryBenchmark(
  forum: ForumBase,
  categoryForums: ForumBase[]
): CategoryBenchmark | null {
  const peers = getCategoryPeers(categoryForums, forum.category);
  if (peers.length < 2) return null;

  const scores = peers.map((f) =>
    calculateForumScore(f.topics_count, f.posts_count, f.members_count, f.founded_year)
  );
  const members = peers.map((f) => f.members_count);
  const posts = peers.map((f) => f.posts_count);
  const avg = (arr: number[]) => arr.reduce((a, b) => a + b, 0) / arr.length;

  const forumScore = calculateForumScore(
    forum.topics_count,
    forum.posts_count,
    forum.members_count,
    forum.founded_year
  );

  return {
    peerCount: peers.length,
    avgMembers: Math.round(avg(members)),
    avgPosts: Math.round(avg(posts)),
    avgScore: Math.round(avg(scores)),
    memberPercentile: percentile(forum.members_count, members),
    postsPercentile: percentile(forum.posts_count, posts),
    scorePercentile: percentile(forumScore, scores),
    memberRank: getForumRank(forum, peers, 'members_count'),
    postsRank: getForumRank(forum, peers, 'posts_count'),
    scoreRank: getForumRank(forum, peers, 'score'),
    forumMembers: forum.members_count,
    forumPosts: forum.posts_count,
    forumScore,
  };
}

export function getMetricComparisons(b: CategoryBenchmark): MetricComparison[] {
  const vsAvg = (forumValue: number, avgValue: number) =>
    avgValue > 0 ? Math.round(((forumValue - avgValue) / avgValue) * 100) : null;

  return [
    {
      key: 'members',
      label: 'Üye',
      forumValue: b.forumMembers,
      avgValue: b.avgMembers,
      percentile: b.memberPercentile,
      rank: b.memberRank,
      total: b.peerCount,
      vsAvgPercent: vsAvg(b.forumMembers, b.avgMembers),
    },
    {
      key: 'posts',
      label: 'İleti',
      forumValue: b.forumPosts,
      avgValue: b.avgPosts,
      percentile: b.postsPercentile,
      rank: b.postsRank,
      total: b.peerCount,
      vsAvgPercent: vsAvg(b.forumPosts, b.avgPosts),
    },
    {
      key: 'score',
      label: 'Forum skoru',
      forumValue: b.forumScore,
      avgValue: b.avgScore,
      percentile: b.scorePercentile,
      rank: b.scoreRank,
      total: b.peerCount,
      vsAvgPercent: vsAvg(b.forumScore, b.avgScore),
    },
  ];
}

/** Yüzdelik dilim metni — üst %0 gibi yanıltıcı ifadelerden kaçınır */
export function formatPercentileHeadline(p: number, rank: number, total: number): string {
  if (rank === 1) return 'Kategoride 1. sırada';
  if (p >= 100) return 'Kategoride zirvede';
  if (p >= 90) return `En büyük %${Math.max(1, total - rank + 1)} içinde`;
  if (p >= 75) return `Üst %${Math.max(1, 100 - p)} diliminde`;
  if (p >= 50) return 'Kategori ortalamasının üzerinde';
  if (p >= 25) return 'Kategori ortalamasına yakın';
  return 'Kategori ortalamasının altında';
}

export function benchmarkSummary(
  b: CategoryBenchmark,
  metric: BenchmarkMetric = 'members'
): string {
  const rank =
    metric === 'members' ? b.memberRank : metric === 'posts' ? b.postsRank : b.scoreRank;
  const p =
    metric === 'members'
      ? b.memberPercentile
      : metric === 'posts'
        ? b.postsPercentile
        : b.scorePercentile;
  const label = metric === 'members' ? 'üye' : metric === 'posts' ? 'ileti' : 'skor';

  return `${formatPercentileHeadline(p, rank, b.peerCount)} (${label} · ${rank}/${b.peerCount})`;
}

export function formatVsAverage(vsAvgPercent: number | null): string {
  if (vsAvgPercent === null) return '—';
  if (vsAvgPercent === 0) return 'Ortalama';
  return `${vsAvgPercent > 0 ? '+' : ''}${vsAvgPercent}% ort.`;
}
